import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InviteDialog } from '~/components/features/team/invite-dialog';
import { TeamList } from '~/components/features/team/team-list';
import { PageContainer } from '~/components/layouts/page-container';
import { Input } from '~/components/ui/input';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';
import { i18n } from '~/lib/i18n';
import { resolveOrgId } from '~/lib/slug-resolver';

function teamQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: ['team', orgId] as const,
    queryFn: async () => {
      const res = await api.api.org[':orgId'].users.$get({ param: { orgId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
      return res.json();
    }
  });
}

export const Route = createFileRoute('/_authenticated/org/$orgSlug/team/')({
  loader: ({ context, params }) => {
    const orgId = resolveOrgId(context.queryClient, params.orgSlug);
    if (orgId) {
      void context.queryClient.prefetchQuery(teamQueryOptions(orgId));
    }
    void context.queryClient.prefetchQuery(departmentsQueryOptions);
  },
  component: TeamPage
});

function TeamPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('team.title'));
  const { orgSlug } = Route.useParams();
  const { orgId } = useSlugIds(orgSlug);
  const [search, setSearch] = useState('');
  const { data } = useQuery({ ...teamQueryOptions(orgId), enabled: !!orgId });
  const { data: deptData } = useQuery(departmentsQueryOptions);
  const { data: session } = useQuery(meQueryOptions);

  const members = data?.members ?? [];
  const departments = deptData?.departments ?? [];

  return (
    <PageContainer
      breadcrumbs={[{ label: t('team.title') }]}
      title={t('team.title')}
      actions={
        session?.role !== 'user' ? (
          <InviteDialog orgId={orgId} departments={departments} />
        ) : undefined
      }
    >
      <Input
        placeholder={t('team.search')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        className="max-w-sm"
      />

      <TeamList
        orgId={orgId}
        members={members}
        search={search}
        currentRole={session?.role ?? 'user'}
        allDepartments={departments}
      />
    </PageContainer>
  );
}
